import { useState, useEffect, useContext, useRef } from 'react'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { DocumentDuplicateIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { EnvelopeIcon, UserPlusIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { setTrainingPeaksIntegrationActive } from 'redux/slices/user'
import trainingPeaksLogo from 'media/integrations/training_peaks/TP_Preferred_Vertical Logo-Blue-Navy-2X.png'
import loadingSpinner from 'media/loading.svg'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import FrequentlyAskedQuestions from 'components/Support/FrequentlyAskesQuestions'

export default function AthleteOnboardingOptionsView() {

    const onboardingOptions = [
        {
            key: 'send-email-invite',
            icon: (
                <div className="h-12 w-12 rounded-full bg-orange-400 flex items-center justify-center">
                    <EnvelopeIcon className="h-8 w-8 text-white" />
                </div>
            ),
            title: 'Send Email Invite',
            description: 'Send an email invite with instructions on how to sign up and join your organization',
            action: () => navigate('/onboarding/athletes/invite')
        },
        {
            key: 'create-athlete-account',
            icon: (
                <div className="h-12 w-12 rounded-full bg-orange-400 flex items-center justify-center">
                    <UserPlusIcon className="h-8 w-8 text-white" />
                </div>
            ),
            title: 'Create Athlete Account',
            description: 'Create an athlete account for a user. They can be invited to sign into it later',
            action: () => navigate('/onboarding/athletes/create')
        },
        {
            key: 'add-athletes-from-training-peaks',
            icon: (
                <div className='px-1'>
                    <img src={trainingPeaksLogo} alt="TrainingPeaks" className="h-10 mb-1 rounded-md" />
                </div>
            ),
            title: 'Add Athletes From TrainingPeaks',
            description: 'Add athletes to your Kinesis organization by connecting to your TrainingPeaks coach account',
            action: () => OnAddAthletesFromTrainingPeaksOptionSelected()
        }
    ]

    const faqs = [
        {
            question: "How does adding athletes to my organization work?",
            answer: [
                "When an organization is created a unique organization code is generated.",
                "This code can be used by athletes on the athlete app to join your organization."
            ]
        },
        {
            question: "Where can I find my unique organization code?",
            answer: [
                "You can find your unique organization code at the top right of this page."
            ]
        },
        {
            question: "What is the difference between sending an athlete an email invite versus just sending them the organization code?",
            answer: [
                "When you send an email invite, it includes detailed instructions on how to access the athlete app and get signed up. This invite email includes the organization code embedded.",
                "When you send just the organization code, it is up to you to further instruct the athlete on how to sign up."
            ]
        },
        {
            question: "Can I preview the invite email before sending it?",
            answer: [
                "Yes. You can preview a plain text version of the invite email by selected the \"Send Email Invite\" option and clicking \"What if I want to send the email myself?\"",
                "This can be useful if you would like to send the more detailed setup instructions out yourself."
            ]
        },
        {
            question: "What is the difference between inviting an athlete versus creating an athlete account?",
            answer: [
                "When you invite an athlete, you are simply sending out an email to that athlete with instructions on how to get setup. It is compltetly up to the athlete to get their account setup and join your organization. You will not see the athlete appear in your organization until they have completed the setup process.",
                "When you create an athlete account it is immediately to your organization. Their profile will be visible in your organization, they can be assigned to a coach, details can be filled out, they will be added to your bill, etc. This allows you to get an athlete setup without waiting for them to sign up. At any point the athlete can be invited to sign into that created account."
            ]
        },
        {
            question: "How do I invite an athlete to sign into an account that I created?",
            answer: [
                "When creating the account you can check a box to also an invite.",
                "Additionally you can navigate to an athletes profile and resend an invite to sign in via the \"Athlete Settings\""
            ]
        },
        {
            question: "Can I post my organization code publicly?",
            answer: [
                "Yes you can. However be aware that anyone who comes across this code can use it to join your organization."
            ]
        },
        {
            question: "Can I change my organization code?",
            answer: [
                "Yes you can. Please reach out to support@kinesisintegrated.com to do so."
            ]
        },
        {
            question: "When a athlete joins my organization, which coach will they be assigned to?",
            answer: [
                "If an athlete is invited to sign up on their own, they will be assigned to no coach by default.",
                "If an athlete account was created by a coach, they will be assigned to that coach by default.",
                "Organization owners and admins can view and reassign any athlete in an organization",
                "Base level coaches will only be able to view athletes that have been assigned to them."
            ]
        }   
    ]

    const hasProcessedTokenExchange = useRef(false)

    const { ShowModal, HideModal } = useContext(ModalContext)

    const navigate = useNavigate()
    const location = useLocation()
    const [searchParams] = useSearchParams()
    const dispatch = useDispatch()

    const userId = useSelector((state) => state.user.id)
    const organizationCode = useSelector((state) => state.user.organizationCode)
    const isTrainingPeaksIntegrationActive = useSelector((state) => state.user.isTrainingPeaksIntegrationActive)

    const [showCopiedText, setShowCopiedText] = useState(false)
    const [showOrganizationCodeTooltip, setShowOrganizationCodeTooltip] = useState(false)
    const [trainingPeaksIntegrationCardLoading, setTrainingPeaksIntegrationCardLoading] = useState(false)

    const CopyOrganizationCodeToClipboard = () => {
        navigator.clipboard.writeText(organizationCode);
        setShowCopiedText(true);
        setTimeout(() => setShowCopiedText(false), 1000);
    }

    const OnAddAthletesFromTrainingPeaksOptionSelected = () => {
        if (isTrainingPeaksIntegrationActive === false) {
            RedirectToTrainingPeaksAuthURL()
        }

        if (isTrainingPeaksIntegrationActive === true) {
            navigate('/onboarding/athletes/trainingPeaks?defaultValue=create-account')
        }
    }

    const RedirectToTrainingPeaksAuthURL = async () => {

        // If already loading, don't allow another request
        if (trainingPeaksIntegrationCardLoading === true) return

        setTrainingPeaksIntegrationCardLoading(true)

        const jsonBody = {
            userId: userId, // Used for authorizer
            redirect_uri: window.location.origin + '/onboarding/athletes/options'
        }

        const result = await WhichWeightAPI('CORE', 'GET', '/training_peaks_auth_url', jsonBody);

        // console.log(result)

        if (result.data.status === 'failure') {
            setTrainingPeaksIntegrationCardLoading(false)
            console.error(result)
            return
        }

        var authURL = result.data.authURL;

        window.location.href = authURL;

        setTrainingPeaksIntegrationCardLoading(false)
    }

    const CallTokenExchangeAPI = async (code) => {

        setTrainingPeaksIntegrationCardLoading(true)

        const jsonBody = {
            userId: userId, // Used for authorizer
            code: code,
            redirect_uri: window.location.origin + '/onboarding/athletes/options'
        }

        // console.log(jsonBody)

        const result = await WhichWeightAPI('CORE', 'POST', '/training_peaks_token_exchange', jsonBody);

        // console.log(result)

        setTrainingPeaksIntegrationCardLoading(false)

        ShowTokenExchangeStatusModal(result.data.status)
    }

    const ShowTokenExchangeStatusModal = (tokenExchangeStatus) => {

        if (tokenExchangeStatus === 'success') {
            navigate('/onboarding/athletes/trainingPeaks?defaultValue=create-account')
            dispatch(setTrainingPeaksIntegrationActive(true))
        }

        if (tokenExchangeStatus === 'failure') {
            var modalData = {
                key: ModalKey.Text,
                fullscreen: false,
                size: 'quarter',
                titleText: 'TrainingPeaks Integration Failed!',
                bodyText: [
                    'TrainingPeaks integration has failed to setup. Please try again.',
                    'If the problem persists, please contact support at support@kinesisintegrated.com',
                ],
                buttonText: 'Ok',
                buttonAction: () => HideModal()
            }

            ShowModal(modalData)

            dispatch(setTrainingPeaksIntegrationActive(false))
        }
    }

    // Handle token exchange when redirected back from TrainingPeaks
    useEffect(() => {
        const code = searchParams.get('code')
        
        if (code && code.length > 0 && !hasProcessedTokenExchange.current) {

            hasProcessedTokenExchange.current = true
            CallTokenExchangeAPI(code)
            navigate(location.pathname, { replace: true }) // Clear the code from URL

        } else if (code === '' && !hasProcessedTokenExchange.current) {

            console.error('No code found in URL')
            hasProcessedTokenExchange.current = true // Handle case where code param exists but is empty
            ShowTokenExchangeStatusModal('failure')
            navigate(location.pathname, { replace: true }) // Clear the code from URL

        }
    }, [searchParams, navigate, location.pathname])

    return (
        <ScreenFrame>
            <div className='flex items-center space-y-1 gap-x-2 pb-2'>
                <h1 className="text-2xl font-semibold text-gray-900 tracking-tight pt-2 flex-grow">
                    How would you like to add athletes?
                </h1>

                <div 
                    className='relative cursor-pointer' 
                    onClick={CopyOrganizationCodeToClipboard}
                >
                    <ContentFrame>
                        <div className='py-2 pl-4 pr-10 text-sm text-center bg-white font-bold'>
                            {organizationCode}
                        </div>
                    </ContentFrame>

                    <div className='absolute inset-y-0 right-4 flex items-center z-20'>
                        <DocumentDuplicateIcon className='h-4 w-4 text-gray-600' />
                    </div>

                    {showCopiedText && (
                        <div className='absolute inset-y-0 w-[90%] flex items-center justify-center bg-white m-1 rounded-lg z-10'>
                            <p className='text-center text-sm font-bold text-gray-500'>Copied!</p>
                        </div>
                    )}
                </div>

                <div 
                    className='relative'
                    onMouseEnter={() => setShowOrganizationCodeTooltip(true)}
                    onMouseLeave={() => setShowOrganizationCodeTooltip(false)}
                >
                    <QuestionMarkCircleIcon className='h-5 w-5 text-gray-500' />

                    {showOrganizationCodeTooltip && (
                        <div className="absolute top-full left-1/2 mt-4 w-40 -translate-x-1/2 transform rounded-md bg-white p-4 text-xs shadow-xl z-30">
                            This is your unique organization code. You can share it with athletes to allow them to join your organization.
                        </div>
                    )}
                </div>
            </div>

            {onboardingOptions.map((option, index) => (
                <ContentFrame key={index}>
                    <div 
                        className="w-full flex items-center p-6 gap-x-6 cursor-pointer hover:bg-gray-50 transition-all"
                        onClick={option.action}
                    >
                        <div>
                            {option.icon}
                        </div>

                        <div className='flex-1 space-y-1'>
                            <div className="text-xl font-semibold">{option.title}</div>
                            <div className="text-sm text-gray-500">{option.description}</div>
                        </div>

                        {option.key !== 'add-athletes-from-training-peaks' && 
                            <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500 transition-colors" />
                        }

                        {option.key === 'add-athletes-from-training-peaks' && 
                            <>
                                {trainingPeaksIntegrationCardLoading === false && (
                                    <ChevronRightIcon className="h-6 w-6 text-gray-400 group-hover:text-orange-500 transition-colors" />
                                )}

                                {trainingPeaksIntegrationCardLoading === true && (
                                    <div className='flex items-center justify-center'>
                                        <img src={loadingSpinner} alt="Loading" className="w-6 h-6 spinner mr-2" />
                                    </div>
                                )}
                            </>
                        }
                    </div>
                </ContentFrame>
            ))}

            <div className='pt-4'>Frequently Asked Questions (FAQs)</div>

            <FrequentlyAskedQuestions faqs={faqs} />

            <p className='pt-4 text-center text-gray-600'>
                Have additional questions? Contact us at <a href="mailto:support@kinesisintegrated.com" className="text-blue-500 underline">support@kinesisintegrated.com</a>
            </p>
        </ScreenFrame>
    )
}
